import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Header from '../feature/Header';
import Footer from '../feature/Footer';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  serviceSchema,
  faqSchema,
  breadcrumbSchema,
  localBusinessSchema,
  videoSchema,
  articleSchema,
} from '../enhancements/PageSchema';
import { SITE_ORIGIN, PHONE, PHONE_DISPLAY, WHATSAPP } from '../../data/cluster';

const PROVIDER_ID = `${SITE_ORIGIN}/#organization`;

export interface SiloLink {
  label: string;
  to: string;
}

export interface ClusterPageProps {
  // SEO core — Goldie: keyword in URL, title, H1, first sentence
  slug: string;            // path without leading slash, e.g. "chatgpt-ads-miami"
  keyword: string;
  title: string;           // full <title> (suffixed with | FAME)
  h1: string;
  eyebrow?: string;
  // 40-60 word lead answer (GEO liftable)
  leadAnswer: string;
  metaDescription: string;
  keywords: string;
  areaServed: string[];
  serviceName: string;
  serviceDescription: string;
  faqs: { q: string; a: string }[];
  breadcrumb: { name: string; url: string }[];
  videoName?: string;
  // Real explainer video (mp4 in /public/videos). When set, a <video> renders in
  // the body and the VideoObject schema points contentUrl at the real file.
  videoSrc?: string;        // e.g. "/videos/fame-chatgpt-ads-miami-roofers.mp4"
  videoPoster?: string;     // e.g. "/videos/fame-chatgpt-ads-miami-roofers.jpg"
  videoUploadDate?: string; // ISO date, defaults 2026-05-29
  // Article freshness/authorship signals for AI search + Google. Defaults applied below.
  datePublished?: string;   // ISO date, defaults 2026-05-29
  dateModified?: string;    // ISO date, defaults to today's content refresh
  // Optional second explainer (e.g. an HVAC cut on a city page). Rendered + adds
  // its own VideoObject for AI-citation coverage of a second priority trade.
  secondaryVideoSrc?: string;
  secondaryVideoPoster?: string;
  secondaryVideoName?: string;
  secondaryVideoDescription?: string;
  secondaryVideoLabel?: string;
  // body sections
  children: ReactNode;
  // silo internal linking
  upLinks?: SiloLink[];        // links UP to hub/sub-hubs
  crossLinks?: SiloLink[];     // sibling cross-links
}

export default function ClusterPage(props: ClusterPageProps) {
  const canonical = `${SITE_ORIGIN}/${props.slug}`;

  const schema = useMemo(() => {
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': PROVIDER_ID,
        name: 'Florida AI Marketing Experts',
        url: SITE_ORIGIN,
        telephone: PHONE,
      },
      localBusinessSchema({
        name: `Florida AI Marketing Experts — ${props.serviceName}`,
        description: props.metaDescription,
        url: canonical,
        areaServed: props.areaServed,
        telephone: PHONE,
      }),
      serviceSchema({
        name: props.serviceName,
        description: props.serviceDescription,
        url: canonical,
        providerId: PROVIDER_ID,
        areaServed: 'Florida',
        priceUnit: 'MONTH',
      }),
      faqSchema(props.faqs),
      articleSchema({
        headline: props.h1,
        description: props.metaDescription,
        url: canonical,
        datePublished: props.datePublished || '2026-05-29',
        dateModified: props.dateModified || '2026-06-14',
        authorName: 'Florida AI Marketing Experts',
      }),
      videoSchema({
        name: props.videoName || `${props.h1} — FAME explainer`,
        description: props.leadAnswer,
        // contentUrl must point at the real media file for AI-citation eligibility.
        url: props.videoSrc ? `${SITE_ORIGIN}${props.videoSrc}` : canonical,
        thumbnailUrl: props.videoPoster ? `${SITE_ORIGIN}${props.videoPoster}` : undefined,
        uploadDate: props.videoUploadDate || '2026-05-29',
      }),
      breadcrumbSchema(props.breadcrumb),
      ...(props.secondaryVideoSrc
        ? [
            videoSchema({
              name: props.secondaryVideoName || `${props.h1} — FAME explainer`,
              description: props.secondaryVideoDescription || props.leadAnswer,
              url: `${SITE_ORIGIN}${props.secondaryVideoSrc}`,
              thumbnailUrl: props.secondaryVideoPoster
                ? `${SITE_ORIGIN}${props.secondaryVideoPoster}`
                : undefined,
              uploadDate: props.videoUploadDate || '2026-05-29',
            }),
          ]
        : []),
    ];
  }, [props, canonical]);

  usePageTitle({
    title: `${props.title} | FAME`,
    description: props.metaDescription,
    keywords: props.keywords,
    canonical,
    schema,
  });

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />

      {/* Hero — keyword in H1 + first sentence (lead answer) */}
      <section className="relative overflow-hidden bg-white grid-bg pt-36 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* Breadcrumb — always renders a link to the site homepage */}
          <nav aria-label="Breadcrumb" className="mb-5 text-sm text-slate-500">
            <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
              Florida AI Marketing Experts
            </Link>
          </nav>
          {props.eyebrow && (
            <div className="eyebrow mb-5">
              {props.eyebrow}
            </div>
          )}
          <h1 className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-[-0.02em] mb-6">
            {props.h1}
          </h1>
          {/* 40-60 word lead answer block — GEO liftable */}
          <p className="text-lg md:text-xl max-w-3xl text-slate-600 font-body font-light mb-8">
            {props.leadAnswer}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/audit"
              className="btn-primary text-center"
            >
              Free AI Marketing Audit
            </Link>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-center"
            >
              Book a ChatGPT Ads Strategy Call →
            </a>
          </div>
          <p className="text-slate-500 text-sm mt-4">
            Own the ChatGPT Answer — WhatsApp {PHONE_DISPLAY}. Monthly retainer, month-to-month.
          </p>
        </div>
      </section>

      {/* Explainer video (vertical Reel/Short) — real mp4 + VideoObject schema */}
      {props.videoSrc && (
        <section className="bg-white pt-12">
          <div className="max-w-md mx-auto px-6">
            <div className="rounded-2xl overflow-hidden shadow-xl border border-navy-100 bg-black">
              <video
                controls
                playsInline
                preload="metadata"
                poster={props.videoPoster}
                className="w-full h-auto block"
                style={{ aspectRatio: '9 / 16' }}
              >
                <source src={props.videoSrc} type="video/mp4" />
              </video>
            </div>
            <p className="text-center text-sm text-navy-600 mt-3">
              60-second explainer: how {props.serviceName} works.
            </p>
          </div>
        </section>
      )}

      {/* Optional secondary explainer (second priority trade, e.g. HVAC) */}
      {props.secondaryVideoSrc && (
        <section className="bg-white pt-8">
          <div className="max-w-md mx-auto px-6">
            <div className="rounded-2xl overflow-hidden shadow-xl border border-navy-100 bg-black">
              <video
                controls
                playsInline
                preload="metadata"
                poster={props.secondaryVideoPoster}
                className="w-full h-auto block"
                style={{ aspectRatio: '9 / 16' }}
              >
                <source src={props.secondaryVideoSrc} type="video/mp4" />
              </video>
            </div>
            <p className="text-center text-sm text-navy-600 mt-3">
              {props.secondaryVideoLabel || '60-second explainer.'}
            </p>
          </div>
        </section>
      )}

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-16 cluster-prose">
        {props.children}
      </div>

      {/* FAQ — matches FAQPage schema */}
      <section className="bg-navy-50 py-16 border-t border-navy-100">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-8 font-display">
            Frequently asked questions
          </h2>
          <div className="space-y-5">
            {props.faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-lg border border-navy-100 p-6">
                <h3 className="font-bold text-navy-900 mb-2">{f.q}</h3>
                <p className="text-navy-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Silo internal linking */}
      {(props.upLinks?.length || props.crossLinks?.length) && (
        <section className="py-14 bg-white border-t border-navy-100">
          <div className="max-w-5xl mx-auto px-6">
            {props.upLinks?.length ? (
              <>
                <h3 className="text-xl font-bold text-navy-900 mb-4 font-display">
                  Part of our ChatGPT Ads for South Florida cluster
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                  {props.upLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="bg-navy-50 rounded-lg p-4 border border-navy-100 hover:border-teal-500 hover:shadow transition text-teal-600 hover:text-teal-700 font-semibold"
                    >
                      {l.label} →
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
            {props.crossLinks?.length ? (
              <>
                <h3 className="text-xl font-bold text-navy-900 mb-4 font-display">
                  Related pages
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {props.crossLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="bg-navy-50 rounded-lg p-3 border border-navy-100 hover:border-teal-500 hover:shadow transition text-sm text-teal-600 hover:text-teal-700 font-medium"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </section>
      )}

      {/* Final CTA — Own the ChatGPT Answer */}
      <section className="py-16 bg-gradient-to-br from-navy-900 to-navy-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 font-display">
            Own the ChatGPT Answer
          </h2>
          <p className="text-lg md:text-xl mb-8 text-white/90">
            Get the verified ChatGPT Ads account, ZIP-targeted campaigns, conversion tracking, and GEO
            so your business is the one ChatGPT names — and the one running the sponsored card beneath
            the answer. Managed monthly by FAME.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/audit"
              className="btn-primary"
            >
              Free AI Marketing Audit →
            </Link>
            <a
              href={WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition"
            >
              Book a Strategy Call · WhatsApp {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* Keyword-anchored closing line — keeps the target phrase in the page's
          final content and links back to the homepage for internal-link equity. */}
      <section className="bg-white border-t border-navy-100 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-500">
            {props.keyword.charAt(0).toUpperCase() + props.keyword.slice(1)} with{' '}
            <Link to="/" className="text-teal-600 hover:text-teal-700 font-medium">
              Florida AI Marketing Experts
            </Link>{' '}
            — WhatsApp {PHONE_DISPLAY}.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Small body helpers for consistent Goldie-style prose (stat callouts, H2s).
export function Stat({ children }: { children: ReactNode }) {
  return (
    <div className="my-6 bg-navy-50 border-l-4 border-teal-500 p-5 rounded-r-lg text-navy-900">
      <span className="font-semibold text-teal-600">Fact: </span>
      {children}
    </div>
  );
}

export function H2({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-navy-900 mt-12 mb-4 font-display">
      {children}
    </h2>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-navy-600 leading-relaxed mb-4 text-lg">{children}</p>;
}
